import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../lib/auth-context.js";
import { ApiError } from "../lib/api.js";
import { listAccounts } from "../lib/api-modules/accounts.js";
import { listBudgetCategories } from "../lib/api-modules/budget-categories.js";
import {
  deleteSpendingTransaction,
  listSpendingTransactions,
} from "../lib/api-modules/spending-transactions.js";
import {
  PAYMENT_METHOD_LABELS,
  type AccountSummary,
  type BudgetCategory,
  type MembershipRole,
  type SpendingTransaction,
} from "../lib/api-modules/types.js";
import { formatCentsCurrency } from "../lib/util/money.js";
import { currentMonth, monthRange, shiftMonth } from "../lib/util/month-range.js";
import { ErrorMessage } from "../components/auth/error-message.js";
import { ConfirmButton } from "../components/confirm-button.js";
import { Toast, useToast } from "../components/toast.js";
import { CategoriesPanel } from "../components/categories-panel.js";
import { SpendingTransactionForm } from "../components/spending/SpendingTransactionForm.js";
import { BudgetVsActualChart } from "../components/spending/BudgetVsActualChart.js";
import { LifestyleInflationTile } from "../components/dashboard/LifestyleInflationTile.js";

/**
 * /spending — household-scoped spending transactions for one month
 * at a time, with category filter + budget-vs-actual chart +
 * inline transaction add form + reusable CategoriesPanel.
 *
 * The page is single-month scoped because budget-vs-actual is a
 * monthly metric and date range pickers add complexity for marginal
 * value. Use the prev/next month buttons to navigate.
 */

const READ_ONLY_ROLES: ReadonlySet<MembershipRole> = new Set([
  "viewer",
  "auditor",
]);

export function SpendingPage() {
  const { user } = useAuth();
  const location = useLocation();
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);

  // Phase 13.5: hash-anchor scroll for `/spending#lifestyle-inflation`.
  // The lifestyle-inflation-flagged SES email's CTA URL ends in this
  // anchor (jobs/src/handlers/lifestyle-inflation-audit.ts); the section
  // below renders only when categories are flagged, so we defer one
  // frame to let the tile mount before scrolling.
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    const tick = globalThis.requestAnimationFrame(() => {
      document
        .getElementById(id)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => globalThis.cancelAnimationFrame(tick);
  }, [location.hash]);

  if (!user) return null;
  const canWrite = !READ_ONLY_ROLES.has(user.role);

  return (
    <div className="mx-auto max-w-6xl p-6">
      <header className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Spending</h1>
          <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
            Your spending, categorized and measured against monthly budgets.
          </p>
        </div>
        {canWrite && (
          <Link
            to="/import"
            className="text-sm font-medium text-brand-600 hover:underline dark:text-brand-400"
          >
            Import files
          </Link>
        )}
      </header>

      {/* Phase 13.5: lifestyle-inflation coaching surface lifted out of
          /dashboard. The tile renders null when no flagged categories
          exist, so this section is invisible until the audit fires. The
          welcome-back-summary email links to `#lifestyle-inflation`. */}
      <section id="lifestyle-inflation" className="mb-6 scroll-mt-6">
        <LifestyleInflationTile today={today} />
      </section>

      {!canWrite && (
        <p className="mb-6 rounded-md border border-zinc-200 bg-zinc-50 p-3 text-sm text-zinc-600 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-400">
          Read-only access — ask an owner or admin to make changes.
        </p>
      )}

      <SpendingPanel canWrite={canWrite} />
    </div>
  );
}

function SpendingPanel({ canWrite }: { canWrite: boolean }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [yearMonth, setYearMonth] = useState<string>(() => currentMonth());
  const [categoryFilter, setCategoryFilter] = useState<string>("");
  const [showAdd, setShowAdd] = useState(false);

  const { from, to } = useMemo(() => monthRange(yearMonth), [yearMonth]);

  const accountsQuery = useQuery({
    queryKey: ["accounts", "list"],
    queryFn: () => listAccounts(),
  });
  const categoriesQuery = useQuery({
    queryKey: ["budget-categories", { includeInactive: false }],
    queryFn: () => listBudgetCategories(false),
  });
  const txQuery = useQuery({
    queryKey: ["spending-transactions", { from, to, categoryFilter }],
    queryFn: () =>
      listSpendingTransactions({
        from,
        to,
        ...(categoryFilter ? { categoryId: categoryFilter } : {}),
      }),
  });

  const deleteMutation = useMutation({
    mutationFn: (args: { date: string; tx_id: string }) =>
      deleteSpendingTransaction(args.date, args.tx_id),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["spending-transactions"],
      });
      toast.show("Transaction deleted");
    },
    onError: (err) => {
      toast.show(
        err instanceof ApiError
          ? `Couldn't delete: ${err.message}`
          : "Couldn't delete transaction",
      );
    },
  });

  const accounts = accountsQuery.data?.accounts ?? [];
  const categories = categoriesQuery.data?.budget_categories ?? [];
  const transactions = txQuery.data?.spending_transactions ?? [];

  const categoryById = useMemo(() => {
    const m = new Map<string, BudgetCategory>();
    for (const c of categories) m.set(c.category_id, c);
    return m;
  }, [categories]);
  const accountById = useMemo(() => {
    const m = new Map<string, AccountSummary>();
    for (const a of accounts) m.set(a.account_id, a);
    return m;
  }, [accounts]);

  const sortedTx = useMemo(
    () => [...transactions].sort((a, b) => b.date.localeCompare(a.date)),
    [transactions],
  );

  const totalCents = transactions.reduce((sum, t) => sum + t.amount_cents, 0);
  const isLoading =
    accountsQuery.isLoading || categoriesQuery.isLoading || txQuery.isLoading;
  const error = accountsQuery.error ?? categoriesQuery.error ?? txQuery.error;

  return (
    <section className="flex flex-col gap-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setYearMonth(shiftMonth(yearMonth, -1))}
            className="rounded-md border border-zinc-300 px-2 py-1 text-xs hover:bg-zinc-50 dark:border-zinc-700 dark:hover:bg-zinc-900"
            aria-label="Previous month"
          >
            ←
          </button>
          <input
            type="month"
            className="rounded-md border border-zinc-300 px-2 py-1 text-sm dark:border-zinc-700 dark:bg-zinc-900"
            value={yearMonth}
            onChange={(e) => setYearMonth(e.target.value)}
            aria-label="Month"
          />
          <button
            type="button"
            onClick={() => setYearMonth(shiftMonth(yearMonth, 1))}
            className="rounded-md border border-zinc-300 px-2 py-1 text-xs hover:bg-zinc-50 dark:border-zinc-700 dark:hover:bg-zinc-900"
            aria-label="Next month"
          >
            →
          </button>
          <select
            className="rounded-md border border-zinc-300 px-2 py-1 text-xs dark:border-zinc-700 dark:bg-zinc-900"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            aria-label="Filter by category"
          >
            <option value="">All categories</option>
            {categories.map((c) => (
              <option key={c.category_id} value={c.category_id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        {canWrite && !showAdd && (
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            className="rounded-md bg-brand-500 px-3 py-1.5 text-xs font-medium text-white shadow-sm transition hover:bg-brand-600"
          >
            Add transaction
          </button>
        )}
      </div>

      {showAdd && (
        <SpendingTransactionForm
          onClose={() => setShowAdd(false)}
          defaultDate={new Date().toISOString().slice(0, 10)}
          accounts={accounts}
          categories={categories}
        />
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-md border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-950">
          <h2 className="mb-3 text-sm font-semibold">Budget vs. Actual</h2>
          <BudgetVsActualChart
            categories={categories}
            transactions={transactions}
          />
        </div>
        <CategoriesPanel />
      </div>

      <div className="rounded-md border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-950">
        <header className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold">
            Transactions{" "}
            <span className="font-normal text-zinc-500">
              · {sortedTx.length} {sortedTx.length === 1 ? "row" : "rows"}
              {sortedTx.length > 0 && (
                <> · {formatCentsCurrency(totalCents)}</>
              )}
            </span>
          </h2>
        </header>
        {isLoading && (
          <p className="text-sm text-zinc-500" role="status">
            Loading…
          </p>
        )}
        {error && (
          <ErrorMessage>
            {error instanceof ApiError
              ? error.message
              : "Couldn't load spending."}
          </ErrorMessage>
        )}
        {!isLoading && !error && sortedTx.length === 0 && (
          <p className="rounded-md border border-dashed border-zinc-300 p-6 text-center text-sm text-zinc-500 dark:border-zinc-700">
            No transactions in this month.
            {canWrite ? " Click \"Add transaction\" to start." : ""}
          </p>
        )}
        {sortedTx.length > 0 && (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-200 text-left text-xs text-zinc-500 dark:border-zinc-800 dark:text-zinc-400">
                <th className="py-2 pr-2 font-normal">Date</th>
                <th className="py-2 pr-2 font-normal">Merchant</th>
                <th className="py-2 pr-2 font-normal">Category</th>
                <th className="py-2 pr-2 font-normal">Account</th>
                <th className="py-2 pr-2 text-right font-normal">Amount</th>
                {canWrite && <th className="py-2 pr-2 font-normal" />}
              </tr>
            </thead>
            <tbody>
              {sortedTx.map((t) => (
                <SpendingTxRow
                  key={t.tx_id}
                  tx={t}
                  category={
                    t.category_id
                      ? categoryById.get(t.category_id)
                      : undefined
                  }
                  account={accountById.get(t.account_id)}
                  canWrite={canWrite}
                  onDelete={() =>
                    deleteMutation.mutate({ date: t.date, tx_id: t.tx_id })
                  }
                  isMutating={deleteMutation.isPending}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Toast message={toast.message} />
    </section>
  );
}

function SpendingTxRow({
  tx,
  category,
  account,
  canWrite,
  onDelete,
  isMutating,
}: {
  tx: SpendingTransaction;
  category: BudgetCategory | undefined;
  account: AccountSummary | undefined;
  canWrite: boolean;
  onDelete: () => void;
  isMutating: boolean;
}) {
  return (
    <tr className="border-b border-zinc-100 last:border-0 dark:border-zinc-900">
      <td className="py-2 pr-2 tabular-nums">{tx.date}</td>
      <td className="py-2 pr-2">
        <div className="font-medium">{tx.merchant}</div>
        {tx.payment_method && (
          <div className="text-xs text-zinc-500">
            {PAYMENT_METHOD_LABELS[tx.payment_method]}
          </div>
        )}
        {tx.description && (
          <div className="text-xs text-zinc-500">{tx.description}</div>
        )}
      </td>
      <td className="py-2 pr-2 text-zinc-600 dark:text-zinc-400">
        {category?.name ?? "—"}
      </td>
      <td className="py-2 pr-2 text-zinc-600 dark:text-zinc-400">
        {account?.name ?? tx.account_id.slice(0, 6) + "…"}
      </td>
      <td className="py-2 pr-2 text-right tabular-nums">
        {formatCentsCurrency(tx.amount_cents)}
      </td>
      {canWrite && (
        <td className="py-2 pr-2 text-right">
          <ConfirmButton
            label="Delete"
            pendingLabel="…"
            size="xs"
            ariaLabel={`Delete ${tx.merchant} ${tx.date}`}
            onConfirm={onDelete}
            isPending={isMutating}
          />
        </td>
      )}
    </tr>
  );
}
